import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { InfoOutlineIcon } from "@chakra-ui/icons";
import {
	Box,
	Button,
	Checkbox,
	Divider,
	FormControl,
	Input,
	Select,
	SimpleGrid,
	Stack,
	Table,
	Tbody,
	Td,
	Text,
	Th,
	Thead,
	Tooltip,
	Tr,
	useDisclosure,
	useToast,
	VStack,
} from "@chakra-ui/react";
import moment from "moment";
import { FiCalendar, FiFileText, FiPlus, FiUpload } from "react-icons/fi";

import { addDocumentAsync, addProtocolAsync, updateAdditionalStepsAsync } from "../../redux/user/userSlice";
import { _arrayBufferToBase64 } from "../../utils/convertors";
import { documentTypes, leaseDocumentTypeIds } from "../../utils/types";
import { addDocumentVerifier } from "../../utils/verifier";
import DocumentsTooltipLabel from "../DocumentsTooltipLabel/DocumentsTooltipLabel";
import Dropzone from "../Dropzone/Dropzone";
import DocumentsTableRow from "../TableRows/DocumentsTableRow";
import ProtocolsTableRow from "../TableRows/ProtocolsTableRow";

import {
	MODAL_GRID_SPACING,
	MODAL_SECTION_SPACING,
	ModalFieldLabel,
	ModalFooterActions,
	PropertyModalSection,
	PropertyModalShell,
} from "./shared";

export default function DocumentsTab({ property }) {
	const isArchived = property.archived;
	const initialDocumentInformation = {
		startingDate: "",
		expirationDate: "",
		documentNumber: "",
		documentType: "",
		documentData: "",
		propertyId: property.id,
		documentName: "",
		contentType: "",
		active: true,
	};
	const initialReportInformation = {
		propertyId: property.id,
		addedDate: moment().format("yyyy-MM-DD"),
		documentNumber: "",
		documentType: "protocol",
		documentData: "",
		documentName: "",
		contentType: "",
		media: [],
	};
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const documents = property.documents.filter((d) => !leaseDocumentTypeIds.includes(d.type));
	const reports = property.protocols;
	const { isOpen, onOpen, onClose } = useDisclosure();
	const { isOpen: isReportOpen, onOpen: onReportOpen, onClose: onReportClose } = useDisclosure();
	const [documentInformation, setDocumentInformation] = useState(initialDocumentInformation);
	const [reportInformation, setReportInformation] = useState(initialReportInformation);
	const [additionalStepsInformation, setAdditionalStepsInformation] = useState(property.additionalSteps);
	const [error, setError] = useState({});
	const toast = useToast();
	useEffect(() => {
		console.log(documentInformation);
	}, [documentInformation]);
	const saveCheckboxes = () => {
		console.log(additionalStepsInformation);
		dispatch(updateAdditionalStepsAsync({ ...additionalStepsInformation, propertyId: property.id })).then((r) => {
			if (r.error?.message)
				toast({
					title: "Error encountered",
					description: r.error.message,
					status: "error",
					duration: 9000,
					isClosable: true,
				});
			else {
				toast({
					title: "Success!",
					description: "Update Successful!",
					status: "success",
					duration: 2000,
					isClosable: true,
				});
			}
		});
	};
	const handleChangeCheckboxes = (e) => {
		setAdditionalStepsInformation({ ...additionalStepsInformation, [e.target.name]: e.target.checked });
	};
	const onDocumentDrop = async (acceptedFiles) => {
		console.log(acceptedFiles);
		const buffer = await acceptedFiles[0].arrayBuffer();
		const documentName = acceptedFiles[0].name;
		const contentType = acceptedFiles[0].type;
		setDocumentInformation({ ...documentInformation, documentData: _arrayBufferToBase64(buffer), documentName, contentType });
	};
	const onMediaDrop = async (acceptedFiles) => {
		const media = [];
		console.log(acceptedFiles);
		for (const file of acceptedFiles) {
			const buffer = await file.arrayBuffer();
			const documentName = file.name;
			const contentType = file.type;
			const doc = { documentData: _arrayBufferToBase64(buffer), documentName, contentType };
			console.log(doc);
			media.push(doc);
		}
		setReportInformation({ ...reportInformation, media });
	};
	const onReportDrop = async (acceptedFiles) => {
		console.log(acceptedFiles);
		const buffer = await acceptedFiles[0].arrayBuffer();
		const documentName = acceptedFiles[0].name;
		const contentType = acceptedFiles[0].type;
		setReportInformation({ ...reportInformation, documentData: _arrayBufferToBase64(buffer), documentName, contentType });
	};
	const addDocument = () => {
		console.log(documentInformation);
		const errors = addDocumentVerifier(documentInformation);
		console.log(errors);
		if (Object.entries(errors).length != 0) {
			setError(errors);
			toast({
				title: "Error encountered.",
				description: "Invalid information",
				status: "error",
				duration: 2000,
				isClosable: true,
			});
			return;
		}
		setError({});
		dispatch(addDocumentAsync(documentInformation))
			.then((r) => {
				if (r.error?.message)
					toast({
						title: "Error encountered",
						description: r.error.message,
						status: "error",
						duration: 9000,
						isClosable: true,
					});
				else {
					toast({
						title: "Success!",
						description: "Document Added!",
						status: "success",
						duration: 2000,
						isClosable: true,
					});
				}
			})
			.finally(() => {
				setDocumentInformation(initialDocumentInformation);
				onClose();
			});
	};
	const addReport = () => {
		console.log(reportInformation);
		const errors = addDocumentVerifier(reportInformation);
		if (Object.entries(errors).length != 0) {
			setError(errors);
			toast({
				title: "Error encountered.",
				description: "Invalid information",
				status: "error",
				duration: 2000,
				isClosable: true,
			});
			return;
		}
		setError({});
		dispatch(addProtocolAsync(reportInformation))
			.then((r) => {
				if (r.error?.message)
					toast({
						title: "Error encountered",
						description: r.error.message,
						status: "error",
						duration: 9000,
						isClosable: true,
					});
				else {
					toast({
						title: "Success!",
						description: "Update successful",
						status: "success",
						duration: 2000,
						isClosable: true,
					});
				}
			})
			.finally(() => {
				onReportClose();
				setReportInformation(initialReportInformation);
			});
	};
	const handleChange = (e) => {
		console.log("changeee");
		setDocumentInformation({ ...documentInformation, [e.target.name]: e.target.value });
	};
	const handleAddReportChange = (e) => {
		console.log("changeee");
		setReportInformation({ ...reportInformation, [e.target.name]: e.target.value });
	};
	const handleCloseDocumentModal = () => {
		onClose();
		setError({});
		setDocumentInformation(initialDocumentInformation);
	};
	const handleCloseReportModal = () => {
		onReportClose();
		setError({});
		setReportInformation(initialReportInformation);
	};

	const renderAddReportModal = () => (
		<PropertyModalShell
			isOpen={isReportOpen}
			onClose={handleCloseReportModal}
			size="3xl"
			icon={FiUpload}
			title={t("property.addReport")}
			subtitle={t("property.addProtocolSubtitle")}
			footer={<ModalFooterActions onCancel={handleCloseReportModal} onConfirm={addReport} confirmLabel={t("misc.add")} />}
		>
			<VStack spacing={MODAL_SECTION_SPACING} align="stretch">
				<PropertyModalSection icon={FiFileText} title={t("property.documentInformation")}>
					<SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
						<FormControl>
							<ModalFieldLabel icon={FiCalendar}>{t("table.addedDate")}</ModalFieldLabel>
							<Input
								size="sm"
								isInvalid={error?.addedDate}
								onChange={handleAddReportChange}
								type="date"
								value={reportInformation.addedDate}
								name="addedDate"
								borderRadius="md"
							/>
						</FormControl>
						<FormControl>
							<ModalFieldLabel icon={FiFileText}>{t("property.documentNumber")}</ModalFieldLabel>
							<Input
								size="sm"
								isInvalid={error?.documentNumber}
								onChange={handleAddReportChange}
								type="text"
								name="documentNumber"
								placeholder={t("property.documentNumberPlaceholder")}
								borderRadius="md"
							/>
						</FormControl>
					</SimpleGrid>
				</PropertyModalSection>
				<PropertyModalSection icon={FiUpload} title={t("property.addProtocol")}>
					<Dropzone
						onDrop={onReportDrop}
						accept={{
							"application/pdf": [".pdf"],
							"application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
							"application/msword": [".doc"],
							"image/*": [],
						}}
						description={t("property.uploadPdfDocDocx")}
						maxFiles={1}
						error={error?.documentData}
					/>
				</PropertyModalSection>
				<PropertyModalSection icon={FiUpload} title={t("property.mediaGallery")}>
					<Dropzone
						onDrop={onMediaDrop}
						accept={{ "image/*": [], "video/*": [] }}
						description={t("property.uploadMediaDescription")}
						error={error?.documentData}
					/>
				</PropertyModalSection>
			</VStack>
		</PropertyModalShell>
	);

	const renderAddDocumentModal = () => (
		<PropertyModalShell
			isOpen={isOpen}
			onClose={handleCloseDocumentModal}
			size="4xl"
			icon={FiPlus}
			title={t("property.addDocument")}
			subtitle={t("property.addDocumentSubtitle")}
			footer={<ModalFooterActions onCancel={handleCloseDocumentModal} onConfirm={addDocument} confirmLabel={t("misc.add")} />}
		>
			<SimpleGrid columns={{ base: 1, lg: 2 }} spacing={MODAL_GRID_SPACING}>
				<VStack spacing={MODAL_SECTION_SPACING} align="stretch">
					<PropertyModalSection icon={FiFileText} title={t("property.documentInformation")}>
						<VStack spacing={3} align="stretch">
							<SimpleGrid columns={2} spacing={4} w="full">
								<FormControl>
									<ModalFieldLabel icon={FiCalendar}>{t("table.startingDate")}</ModalFieldLabel>
									<Input
										size="sm"
										isInvalid={error?.startingDate}
										onChange={handleChange}
										type="date"
										name="startingDate"
										borderRadius="md"
									/>
								</FormControl>
								<FormControl>
									<ModalFieldLabel icon={FiCalendar}>{t("table.expirationDate")}</ModalFieldLabel>
									<Input
										size="sm"
										isInvalid={error?.expirationDate}
										onChange={handleChange}
										type="date"
										name="expirationDate"
										borderRadius="md"
									/>
								</FormControl>
							</SimpleGrid>
							<FormControl>
								<ModalFieldLabel icon={FiFileText}>{t("property.documentNumber")}</ModalFieldLabel>
								<Input
									size="sm"
									isInvalid={error?.documentNumber}
									onChange={handleChange}
									type="text"
									name="documentNumber"
									placeholder={t("property.documentNumberPlaceholder")}
									borderRadius="md"
								/>
							</FormControl>
							<FormControl>
								<ModalFieldLabel icon={FiFileText}>{t("property.type")}</ModalFieldLabel>
								<Select
									size="sm"
									isInvalid={error?.documentType}
									onChange={handleChange}
									name="documentType"
									placeholder={t("property.type")}
									borderRadius="md"
								>
									{documentTypes.map((d, index) => (
										<option key={index} value={d.id}>
											{d.label}
										</option>
									))}
								</Select>
							</FormControl>
							<Checkbox
								name="active"
								onChange={(e) => {
									setDocumentInformation({ ...documentInformation, active: e.target.checked });
								}}
								isChecked={documentInformation.active}
							>
								<Text fontSize="sm">{t("property.isActiveDocument")}</Text>
							</Checkbox>
						</VStack>
					</PropertyModalSection>
				</VStack>
				<VStack spacing={MODAL_SECTION_SPACING} align="stretch">
					<PropertyModalSection icon={FiUpload} title={t("property.documents")}>
						<Dropzone
							onDrop={onDocumentDrop}
							accept={{
								"application/pdf": [".pdf"],
								"application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
								"application/msword": [".doc"],
								"image/*": [],
							}}
							description={t("property.uploadPdfDocDocx")}
							maxFiles={1}
							error={error?.documentData}
						/>
					</PropertyModalSection>
				</VStack>
			</SimpleGrid>
		</PropertyModalShell>
	);

	return (
		<Stack>
			<Stack direction={"row"}>
				<Button onClick={onOpen} colorScheme="blue" width={"fit-content"} isDisabled={isArchived}>
					{t("property.addDocument")}
				</Button>
				<Button onClick={onReportOpen} colorScheme="blue" width={"fit-content"} isDisabled={isArchived}>
					{t("property.addProtocol")}
				</Button>
			</Stack>

			<Stack>
				<Checkbox
					width="fit-content"
					name="tenantAssociationAnnouncement"
					isChecked={additionalStepsInformation.tenantAssociationAnnouncement}
					onChange={handleChangeCheckboxes}
				>
					<Text>{t("property.tenantAssociationNotification")}</Text>
				</Checkbox>
				<Box>
					<Button onClick={saveCheckboxes} colorScheme="blue" isDisabled={isArchived}>
						{t("misc.save")}
					</Button>
				</Box>
			</Stack>
			<Divider />
			{renderAddDocumentModal()}
			{renderAddReportModal()}

			<Box overflowX="auto">
				<Text fontWeight={"bold"} fontSize={"large"} margin={"20px 0px 20px 0px"}>
					{t("property.documents")}{" "}
					<Tooltip label={<DocumentsTooltipLabel property={property} />}>
						<InfoOutlineIcon />
					</Tooltip>
				</Text>

				<Table variant="simple" size="sm">
					<Thead>
						<Tr pl="0px" whiteSpace={"nowrap"}>
							<Th pl="0px">{t("table.addedDate")}</Th>
							<Th>{t("table.name")}</Th>
							<Th>{t("table.type")}</Th>
							<Th>{t("table.number")}</Th>
							<Th>{t("table.startingDate")}</Th>
							<Th>{t("table.expirationDate")}</Th>
							<Th>{t("table.actions")}</Th>
						</Tr>
					</Thead>
					<Tbody>
						{documents.length !== 0 ? (
							<>
								{documents
									.filter((d) => d.active)
									.map((row, index) => {
										return <DocumentsTableRow key={index} row={row} propertyId={property.id} />;
									})}
								{documents
									.filter((d) => !d.active)
									.map((row, index) => {
										return <DocumentsTableRow key={index} row={row} propertyId={property.id} />;
									})}
							</>
						) : (
							<Tr>
								<Td>{t("table.noData")}</Td>
							</Tr>
						)}
					</Tbody>
				</Table>
			</Box>
			<Box overflowX="auto">
				<Text fontWeight={"bold"} fontSize={"large"} margin={"20px 0px 20px 0px"}>
					{t("property.protocols")}
				</Text>
				<Table variant="simple" size="sm">
					<Thead>
						<Tr my=".8rem" pl="0px">
							<Th pl="0px">{t("table.addedDate")}</Th>
							<Th>{t("table.name")}</Th>
							<Th>{t("table.type")}</Th>
							<Th>{t("table.number")}</Th>
							<Th>{t("table.actions")}</Th>
						</Tr>
					</Thead>
					<Tbody>
						{reports?.length !== 0 ? (
							reports?.map((row, index) => {
								return <ProtocolsTableRow key={index} row={row} propertyId={property.id} />;
							})
						) : (
							<Tr>
								<Td colSpan={6}>{t("table.noData")}</Td>
							</Tr>
						)}
					</Tbody>
				</Table>
			</Box>
		</Stack>
	);
}
